import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp, Settings2, Bot, FileText, Newspaper,
  Landmark, Brain, Target, Layers, Zap, Briefcase,
  ArrowRight, Lock, ChevronRight, BarChart3, Shield, Sparkles
} from 'lucide-react';
import { SearchBar } from '../components/ui/SearchBar';
import { useAuth } from '../contexts/AuthContext';
import { tradierService } from '../services';
import { useToast } from '../hooks/useToast';

export const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchLoading, setSearchLoading] = useState(false);

  const handleStockSearch = async (symbol: string) => {
    if (!user) {
      navigate('/auth/signup');
      return;
    }
    setSearchLoading(true);
    try {
      const quotes = await tradierService.instance.getQuotes([symbol.toUpperCase()]);
      if (quotes.length > 0) {
        navigate(`/stock/${symbol.toUpperCase()}`);
      } else {
        showToast(`Symbol ${symbol.toUpperCase()} not found`, 'error');
      }
    } catch {
      showToast('Failed to search. Please try again.', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const analysisSteps = [
    { step: 1, title: 'Moving Averages', description: 'Golden/Death Cross via 50 & 200-day EMAs', icon: TrendingUp, tier: 'Basic' },
    { step: 2, title: 'Technical Indicators', description: 'Advanced indicators with composite scoring', icon: Settings2, tier: 'Moderate' },
    { step: 3, title: 'Reinforcement Learning', description: 'RL framework for quantitative signals', icon: Bot, tier: 'Moderate' },
    { step: 4, title: 'Financial Statements', description: 'SEC filings & insider trading analysis', icon: FileText, tier: 'Advanced' },
    { step: 5, title: 'Sentiment Analysis', description: 'Real-time news & market psychology', icon: Newspaper, tier: 'Advanced' },
    { step: 6, title: 'Economic Data', description: 'Macro indicators via FRED integration', icon: Landmark, tier: 'Advanced' },
    { step: 7, title: 'LLM Market Analysis', description: 'Comprehensive synthesis of all layers', icon: Brain, tier: 'Expert' },
    { step: 8, title: 'AI Final Summary', description: 'Senior portfolio manager recommendation', icon: Target, tier: 'Expert' },
  ];

  const tierMeta: Record<string, { color: string; bg: string }> = {
    Basic:    { color: 'text-slate-400',   bg: 'bg-slate-500/10' },
    Moderate: { color: 'text-blue-400',    bg: 'bg-blue-500/10' },
    Advanced: { color: 'text-violet-400',  bg: 'bg-violet-500/10' },
    Expert:   { color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  };

  const features = [
    {
      icon: Layers,
      title: '8-Layer AI Pipeline',
      description: 'From moving averages to LLM reasoning — every signal condensed into one confident recommendation.',
      accent: '#3b82f6',
    },
    {
      icon: Zap,
      title: 'Real-Time Data',
      description: 'Live quotes, breaking news, and macroeconomic indicators refreshed every 30 seconds.',
      accent: '#10b981',
    },
    {
      icon: Briefcase,
      title: 'Portfolio Tracking',
      description: 'Monitor your holdings with P&L charts, watchlists, and drag-and-drop organisation.',
      accent: '#8b5cf6',
    },
    {
      icon: Shield,
      title: 'Powered by AWS Bedrock',
      description: 'Enterprise-grade AI infrastructure — the same stack used by institutional investors.',
      accent: '#f59e0b',
    },
  ];

  const tickers = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'META', 'NFLX'];

  return (
    <div
      className="page-container"
      style={{ background: 'linear-gradient(160deg, #06091a 0%, #0d1628 40%, #060b14 100%)' }}
    >
      <a href="#main-content" className="skip-to-content">Skip to main content</a>

      {/* ── Ambient blobs ── */}
      <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
        <div style={{
          position: 'absolute', top: '-10%', left: '20%',
          width: 600, height: 600,
          background: 'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', top: '40%', right: '-5%',
          width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', bottom: '10%', left: '10%',
          width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ NAV ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <nav
        aria-label="Main navigation"
        style={{
          position: 'relative', zIndex: 10,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          backdropFilter: 'blur(12px)',
          background: 'rgba(10,14,26,0.6)',
        }}
      >
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 1.5rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 16px rgba(59,130,246,0.35)',
            }}>
              <BarChart3 size={18} color="#fff" strokeWidth={2.5} />
            </div>
            <span style={{ fontSize: '1.125rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em' }}>
              Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
            </span>
          </div>

          {/* Nav actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {user ? (
              <Link
                to="/dashboard"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: '#fff', padding: '0.5rem 1.125rem',
                  borderRadius: 8, fontWeight: 600, fontSize: '0.875rem',
                  transition: 'opacity 0.18s',
                }}
              >
                Go to Dashboard <ArrowRight size={15} />
              </Link>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  style={{ color: '#94a3b8', fontSize: '0.875rem', fontWeight: 500, padding: '0.5rem 0.75rem', transition: 'color 0.18s' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#f1f5f9')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/signup"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                    color: '#fff', padding: '0.5rem 1.125rem',
                    borderRadius: 8, fontWeight: 600, fontSize: '0.875rem',
                    boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                  }}
                >
                  Get Started <ChevronRight size={15} />
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <main id="main-content" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '5rem 1.5rem 4rem' }}>
          <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto' }}>

            {/* Pill label */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 99, padding: '0.3rem 0.9rem', marginBottom: '1.75rem' }}>
              <Sparkles size={13} color="#60a5fa" />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#60a5fa', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Powered by AWS Bedrock AgentCore
              </span>
            </div>

            {/* Headline */}
            <h1 style={{ fontSize: 'clamp(2.25rem, 5.5vw, 3.75rem)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.04em', color: '#f1f5f9', marginBottom: '1rem' }}>
              Know exactly when to{' '}
              <span style={{
                background: 'linear-gradient(120deg, #60a5fa 0%, #a78bfa 50%, #34d399 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>
                Buy, Hold, or Sell
              </span>
            </h1>

            {/* Sub-headline */}
            <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', color: '#94a3b8', lineHeight: 1.7, marginBottom: '3rem', maxWidth: 600, margin: '0 auto 3rem' }}>
              8 layers of AI analysis — moving averages, RL signals, SEC filings, LLM reasoning — distilled into one confident recommendation.
            </p>

            {/* ── Search card ── */}
            <section aria-label="Stock search" style={{ marginBottom: '1.75rem' }}>
              <div style={{
                background: 'rgba(17,24,39,0.75)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 18,
                padding: '2rem',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
                maxWidth: 640, margin: '0 auto',
              }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
                  Analyze any stock
                </h2>

                {!user && (
                  <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#f59e0b', marginBottom: '1rem' }}>
                    <Lock size={12} />
                    Free account required — takes 30 seconds.{' '}
                    <Link to="/auth/signup" style={{ color: '#f59e0b', textDecoration: 'underline', marginLeft: 2 }}>Sign up</Link>
                  </p>
                )}
                {user && (
                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
                    Enter a ticker to run the full 8-step pipeline
                  </p>
                )}

                <div style={{ position: 'relative' }}>
                  <SearchBar
                    onSelect={(result) => handleStockSearch(result.symbol)}
                    placeholder="Search ticker — AAPL, MSFT, TSLA..."
                    className="w-full"
                    disabled={searchLoading}
                  />
                  {searchLoading && (
                    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} aria-live="polite">
                      <div style={{ width: 18, height: 18, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Popular tickers */}
            <section aria-label="Popular stocks" style={{ marginBottom: '5rem' }}>
              <p style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.875rem' }}>
                Quick search
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem' }}>
                {tickers.map(sym => (
                  <button
                    key={sym}
                    onClick={() => handleStockSearch(sym)}
                    disabled={searchLoading}
                    aria-label={`Analyze ${sym}`}
                    style={{
                      padding: '0.375rem 0.875rem',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      color: '#94a3b8',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      cursor: 'pointer',
                      transition: 'all 0.18s',
                      letterSpacing: '0.02em',
                    }}
                    onMouseEnter={e => {
                      const t = e.currentTarget;
                      t.style.background = 'rgba(59,130,246,0.12)';
                      t.style.borderColor = 'rgba(59,130,246,0.35)';
                      t.style.color = '#60a5fa';
                    }}
                    onMouseLeave={e => {
                      const t = e.currentTarget;
                      t.style.background = 'rgba(255,255,255,0.04)';
                      t.style.borderColor = 'rgba(255,255,255,0.08)';
                      t.style.color = '#94a3b8';
                    }}
                  >
                    {sym}
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* ── Stats strip ── */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '1px', background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.05)', borderRadius: 14,
            overflow: 'hidden', maxWidth: 860, margin: '0 auto', marginBottom: '6rem',
          }}>
            {[
              { value: '8', label: 'AI Analysis Layers' },
              { value: '30s', label: 'Data Refresh Rate' },
              { value: '∞', label: 'Stock Coverage' },
              { value: '1', label: 'Final Recommendation' },
            ].map((stat, i) => (
              <div
                key={i}
                style={{ background: 'rgba(17,24,39,0.6)', padding: '1.25rem 1.5rem', textAlign: 'center', backdropFilter: 'blur(8px)' }}
              >
                <div style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.04em', color: '#f1f5f9', lineHeight: 1, marginBottom: '0.35rem', fontFamily: "'JetBrains Mono', monospace" }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ FEATURES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <section
          aria-label="Features"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.05)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            background: 'rgba(10,14,26,0.5)',
            backdropFilter: 'blur(8px)',
            padding: '5rem 1.5rem',
          }}
        >
          <div style={{ maxWidth: 1440, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
                Why Nitrextrader
              </p>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.75rem' }}>
                Built for serious investors
              </h2>
              <p style={{ fontSize: '1rem', color: '#64748b', maxWidth: 520, margin: '0 auto' }}>
                Institutional-grade analysis, accessible to everyone.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              {features.map((f, i) => (
                <article
                  key={i}
                  style={{
                    background: 'rgba(17,24,39,0.8)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 14,
                    padding: '1.75rem',
                    transition: 'border-color 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget;
                    el.style.borderColor = f.accent + '40';
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget;
                    el.style.borderColor = 'rgba(255,255,255,0.06)';
                    el.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 11,
                    background: f.accent + '18',
                    border: `1px solid ${f.accent}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '1.125rem',
                  }}>
                    <f.icon size={20} color={f.accent} strokeWidth={1.75} />
                  </div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
                    {f.title}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.65 }}>
                    {f.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ 8-STEP PIPELINE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <section aria-label="AI analysis pipeline" style={{ padding: '5rem 1.5rem' }}>
          <div style={{ maxWidth: 1440, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
                How It Works
              </p>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.75rem' }}>
                8-Step AI Analysis Pipeline
              </h2>
              <p style={{ fontSize: '1rem', color: '#64748b', maxWidth: 540, margin: '0 auto' }}>
                Every stock runs through all 8 layers in sequence before producing a recommendation.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.875rem' }}>
              {analysisSteps.map((step) => {
                const meta = tierMeta[step.tier];
                return (
                  <article
                    key={step.step}
                    style={{
                      background: 'rgba(17,24,39,0.75)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 14,
                      padding: '1.5rem',
                      backdropFilter: 'blur(8px)',
                      transition: 'border-color 0.2s, background 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)';
                      e.currentTarget.style.background = 'rgba(17,24,39,0.95)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.background = 'rgba(17,24,39,0.75)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 9,
                        background: 'rgba(139,92,246,0.12)',
                        border: '1px solid rgba(139,92,246,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <step.icon size={17} color="#a78bfa" strokeWidth={1.75} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e3a5f', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
                          {String(step.step).padStart(2, '0')}
                        </span>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0.2rem 0.5rem', borderRadius: 99 }}
                          className={`${meta.bg} ${meta.color}`}>
                          {step.tier}
                        </span>
                      </div>
                    </div>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#e2e8f0', marginBottom: '0.375rem', letterSpacing: '-0.01em' }}>
                      {step.title}
                    </h3>
                    <p style={{ fontSize: '0.8125rem', color: '#475569', lineHeight: 1.6 }}>
                      {step.description}
                    </p>
                  </article>
                );
              })}
            </div>

            {/* Final output block */}
            <div style={{
              marginTop: '2rem',
              background: 'linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(59,130,246,0.07) 50%, rgba(239,68,68,0.07) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              padding: '2.25rem',
              textAlign: 'center',
            }}>
              <Target size={32} color="#10b981" style={{ margin: '0 auto 1rem' }} strokeWidth={1.5} />
              <h3 style={{ fontSize: '1.375rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
                Final AI Recommendation
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem', maxWidth: 480, margin: '0 auto 1.5rem' }}>
                All 8 layers synthesised by an LLM into a single, clearly explained verdict.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'BUY',  bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.35)', color: '#34d399' },
                  { label: 'HOLD', bg: 'rgba(245,158,11,0.15)',  border: 'rgba(245,158,11,0.35)',  color: '#fbbf24' },
                  { label: 'SELL', bg: 'rgba(239,68,68,0.15)',   border: 'rgba(239,68,68,0.35)',   color: '#f87171' },
                ].map(r => (
                  <div key={r.label} style={{
                    padding: '0.625rem 2rem',
                    background: r.bg,
                    border: `1px solid ${r.border}`,
                    borderRadius: 10,
                    fontWeight: 800,
                    fontSize: '0.9375rem',
                    color: r.color,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '0.1em',
                  }}>
                    {r.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <section
          style={{
            borderTop: '1px solid rgba(255,255,255,0.05)',
            background: 'rgba(10,14,26,0.7)',
            padding: '5rem 1.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.875rem' }}>
              Start your analysis today
            </h2>
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '2rem', lineHeight: 1.7 }}>
              Free to sign up. No credit card required. Get your first AI recommendation in under a minute.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.875rem', flexWrap: 'wrap' }}>
              {!user ? (
                <>
                  <Link
                    to="/auth/signup"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      color: '#fff', padding: '0.875rem 1.75rem',
                      borderRadius: 10, fontWeight: 700, fontSize: '0.9375rem',
                      boxShadow: '0 8px 24px rgba(59,130,246,0.35)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Create Free Account <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/auth/login"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#94a3b8', padding: '0.875rem 1.75rem',
                      borderRadius: 10, fontWeight: 600, fontSize: '0.9375rem',
                    }}
                  >
                    Sign In
                  </Link>
                </>
              ) : (
                <Link
                  to="/dashboard"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                    color: '#fff', padding: '0.875rem 1.75rem',
                    borderRadius: 10, fontWeight: 700, fontSize: '0.9375rem',
                    boxShadow: '0 8px 24px rgba(59,130,246,0.35)',
                  }}
                >
                  Go to Dashboard <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ FOOTER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <footer style={{ borderTop: '1px solid rgba(255,255,255,0.04)', padding: '2rem 1.5rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart3 size={12} color="#fff" strokeWidth={2.5} />
            </div>
            <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569', letterSpacing: '-0.01em' }}>
              Nitrex<span style={{ color: '#1e3a5f' }}>trader</span>
            </span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#334155', maxWidth: 480, margin: '0 auto' }}>
            For informational purposes only. Not financial advice. Past performance does not guarantee future results. Always consult a qualified financial advisor.
          </p>
        </footer>
      </main>

      {/* spin keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
